<template>
  <div class="min-h-screen bg-white flex flex-col">
    <TrustBar />
    <main class="flex-1 pt-[120px] pb-12">
      <div class="max-w-4xl mx-auto px-4">
        <h1 class="text-3xl font-bold text-gray-900 mb-6">Politique de Confidentialité</h1>

        <div class="prose prose-gray max-w-none">
          <section class="mb-8">
            <p class="text-gray-600 mb-4">
              <strong>Dernière mise à jour :</strong> Janvier 2025
            </p>
            <p class="text-gray-600 mb-4">
              La présente Politique de Confidentialité décrit comment Fluxbuy OÜ (code d'immatriculation : 17222874) collecte, utilise et protège vos données personnelles sur le site Fluxreload.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. Données collectées</h2>

            <h3 class="text-xl font-semibold text-gray-800 mb-2">1.1. Données fournies par vous</h3>
            <p class="text-gray-600 mb-4">Nous collectons uniquement les données nécessaires au traitement de vos commandes :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2 mb-4">
              <li><strong>Adresse email</strong> : pour la livraison des codes de cartes cadeaux et recharges</li>
              <li><strong>Informations de paiement</strong> : traitées exclusivement par nos prestataires de paiement sécurisés</li>
            </ul>

            <h3 class="text-xl font-semibold text-gray-800 mb-2">1.2. Données techniques collectées automatiquement</h3>
            <p class="text-gray-600 mb-4">Certaines données sont collectées automatiquement pour le bon fonctionnement du service :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2 mb-4">
              <li><strong>Adresse IP</strong> : pour la géolocalisation et la sécurité des transactions</li>
              <li><strong>Type d'appareil et navigateur</strong> : pour optimiser l'affichage du site</li>
              <li><strong>Préférences locales</strong> : pays et devise mémorisés localement sur votre appareil</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Utilisation des données</h2>
            <p class="text-gray-600 mb-4">Vos données sont utilisées uniquement pour :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li>Traiter vos commandes et livrer les codes achetés par email</li>
              <li>Assurer la sécurité et la prévention des fraudes</li>
              <li>Améliorer la qualité de notre service</li>
              <li>Respecter nos obligations légales et fiscales</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. Paiement sécurisé</h2>
            <p class="text-gray-600 mb-4">
              Nous utilisons des prestataires de paiement certifiés PCI-DSS pour traiter toutes les transactions. Vos informations de paiement (numéro de carte, coordonnées bancaires) ne sont <strong>jamais stockées</strong> sur nos serveurs.
            </p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Stripe</strong> : traitant les paiements par carte bancaire</li>
              <li><strong>PayPal</strong> : traitant les paiements via compte PayPal</li>
            </ul>
            <p class="text-gray-600 mt-4">
              Ces prestataires respectent les normes de sécurité les plus strictes (PCI-DSS Level 1) pour protéger vos données financières.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Conservation des données</h2>
            <p class="text-gray-600 mb-4">
              Vos données sont conservées uniquement pendant la durée nécessaire aux finalités décrites ci-dessus, conformément aux exigences légales en matière de conservation des documents commerciaux.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Partage des données</h2>
            <p class="text-gray-600 mb-4">Nous ne vendons pas vos données personnelles. Elles peuvent être partagées uniquement avec :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li>Nos prestataires de paiement (Stripe, PayPal) dans le cadre des transactions</li>
              <li>Nos fournisseurs de services techniques nécessaires au fonctionnement du site</li>
              <li>Les autorités compétentes en cas d'obligation légale</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Vos droits</h2>
            <p class="text-gray-600 mb-4">Conformément à la législation, vous disposez des droits suivants :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Droit d'accès</strong> : connaître les données que nous détenons sur vous</li>
              <li><strong>Droit de rectification</strong> : corriger les données inexactes</li>
              <li><strong>Droit à l'effacement</strong> : demander la suppression de vos données (dans la limite des obligations légales)</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition</strong> : vous opposer au traitement de vos données</li>
            </ul>
            <p class="text-gray-600 mt-4">
              Pour exercer ces droits, contactez-nous via notre formulaire de contact.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Sécurité des données</h2>
            <p class="text-gray-600 mb-4">
              Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données contre la perte, le vol, l'accès non autorisé ou la divulgation. Cependant, aucune méthode de transmission sur Internet n'est 100% sécurisée.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Modifications</h2>
            <p class="text-gray-600">
              Nous nous réservons le droit de modifier cette politique à tout moment. Les modifications seront publiées sur cette page avec la date de mise à jour.
            </p>
          </section>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref } from 'vue';
</script>
